import React, { useEffect, useRef } from 'react';
import { FaStar, FaRegStar } from 'react-icons/fa';
import styles from './bodywon.module.css';

const SummaryStars = ({ rating }) => (
  <div className={styles.ratingBreakdownStars} aria-hidden="true">
    {[1, 2, 3, 4, 5].map((value) => {
      const filled = rating >= value;
      const partial = !filled && rating >= value - 0.75;
      return (
        <span
          key={value}
          className={`${styles.ratingBreakdownStar}${partial ? ` ${styles.ratingBreakdownStarPartial}` : ''}`}
          style={partial ? { '--star-fill': `${Math.max(0, Math.min(100, (rating - (value - 1)) * 100))}%` } : undefined}
        >
          {filled || partial ? <FaStar /> : <FaRegStar />}
        </span>
      );
    })}
  </div>
);

const RatingBreakdownPopover = ({
  open,
  onClose,
  onMouseEnter,
  onMouseLeave,
  closeOnOutsideClick = true,
  rating,
  count,
  distribution,
  onSeeReviews,
}) => {
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose?.();
    };

    const handleOutside = (e) => {
      if (!closeOnOutsideClick) return;
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        onClose?.();
      }
    };

    document.addEventListener('keydown', handleEscape);
    if (closeOnOutsideClick) {
      document.addEventListener('mousedown', handleOutside);
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      if (closeOnOutsideClick) {
        document.removeEventListener('mousedown', handleOutside);
      }
    };
  }, [open, onClose, closeOnOutsideClick]);

  if (!open || !count) return null;

  return (
    <div
      className={styles.ratingBreakdownPopover}
      ref={panelRef}
      role="dialog"
      aria-label="Rating breakdown"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <button type="button" className={styles.ratingBreakdownClose} onClick={onClose} aria-label="Close">
        &times;
      </button>

      <div className={styles.ratingBreakdownHeader}>
        <SummaryStars rating={rating} />
        <div className={styles.ratingBreakdownAverage}>
          <strong>{rating.toFixed(1)}</strong> out of 5
        </div>
      </div>

      <p className={styles.ratingBreakdownTotal}>{count} global rating{count === 1 ? '' : 's'}</p>

      <div className={styles.ratingBreakdownRows}>
        {[5, 4, 3, 2, 1].map((star) => (
          <div key={star} className={styles.ratingBreakdownRow}>
            <span className={styles.ratingBreakdownStarLabel}>{star} star</span>
            <div className={styles.ratingBreakdownBarTrack}>
              <div
                className={styles.ratingBreakdownBarFill}
                style={{ width: `${distribution?.[star] ?? 0}%` }}
              />
            </div>
            <span className={styles.ratingBreakdownPercent}>{distribution?.[star] ?? 0}%</span>
          </div>
        ))}
      </div>

      <button type="button" className={styles.ratingBreakdownSeeReviews} onClick={onSeeReviews}>
        See customer reviews &rsaquo;
      </button>
    </div>
  );
};

export default RatingBreakdownPopover;
